import { FC, useContext, useEffect, useState, ReactNode, useMemo } from "react";
import QuestContext from "../../../../components/QuestWrapper";
import { DailyStreakProps, StepDetail } from "../component/StreakTypes";
import "../component/streak.css";
import {
  DEFAULT_BRAND_THEME,
  DEFAULT_QUEST_THEME,
  PartialBrandTheme,
  PartialQuestThemeData,
} from "../../tutorial/components/tutorial.types";
import StreakApi from "../component/StreakApi";
import StreakThemeManager from "../component/StreakThemeManager";
import { StreakContext } from "./StreakContextType";
import { StreakCounter } from "./StreakCounter";
import { StreakContent } from "./StreakContent";
import { StreakFooter } from "./StreakFooter";
import { StreakTimer } from "./StreakTimer";
import { confetti } from "../../../../components/Confetti/confetti";
import { useGeneral } from "../../../../general";

interface StreakComponentChildren {
  Counter: typeof StreakCounter;
  Content: typeof StreakContent;
  Timer: typeof StreakCounter;
  Footer: typeof StreakFooter;
}

interface StreakComponents extends DailyStreakProps {
  children?: ReactNode;
}

const defaultStepDetails: StepDetail[] = [
  { range: 1, title: "Day 1", description: "" },
  { range: 2, title: "Day 2", description: "" },
  { range: 3, title: "Day 3", description: "" },
  { range: 4, title: "Day 4", description: "" },
  { range: 5, title: "Day 5", description: "" },
];

const Streak: FC<StreakComponents> & StreakComponentChildren = ({
  pendingStreakImg = "",
  filledStreakImg = "",
  counterId = "",
  userId = "",
  token = "",
  counter = 0,
  stepDetails = defaultStepDetails,
  uniqueEmailId,
  uniqueUserId,
  styleConfig,
  footerText,
  footerLink,
  questId,
  variation,
  brandTheme: initialBrandTheme,
  QuestThemeData: initialQuestThemeData,
  children,
  checkedUponCompletion,
  confettiOnCompletion,
  confettiDuration,
  entityAuthenticationToken,
  ...props
}) => {
  const [steps, setSteps] = useState(() => stepDetails.sort((a, b) => a.range - b.range));
  const [isLoading, setIsLoading] = useState(true);
  const [days, setDays] = useState(counter);
  const { apiKey, entityId, apiType, themeConfig, user } = useContext(
    QuestContext.Context
  );
  const [showFooter,setShowFooter] = useState(true);
  const dailyStreakApi = new StreakApi(apiType);

  const [questThemeData, setQuestThemeData] = useState<PartialQuestThemeData>(
    initialQuestThemeData || DEFAULT_QUEST_THEME
  );
  const [brandTheme, setBrandTheme] = useState<PartialBrandTheme>(
    initialBrandTheme || DEFAULT_BRAND_THEME
  );
  const general = useGeneral('quest')

  // Create theme manager using useMemo
  const themeManager = useMemo(() => {
    return new StreakThemeManager(
      themeConfig,
      styleConfig,
      questThemeData,
      brandTheme
    );
  }, [themeConfig, styleConfig, questThemeData, brandTheme]);

  // Effect to handle theme prop updates
  useEffect(() => {
    if (initialBrandTheme) {
      setBrandTheme(initialBrandTheme);
    }
    if (initialQuestThemeData) {
      setQuestThemeData(initialQuestThemeData);
    }
  }, [initialBrandTheme, initialQuestThemeData]);

  useEffect(() => {
    const initializeStreak = async () => {
      setIsLoading(true);
      dailyStreakApi.trackEvent("quest_daily_streak_loaded", "daily_streak");
      questId && general.trackEvent(
        'quest-daily-streak-loaded',
        (uniqueUserId || userId || user.userId)!,
        {},
        true,
        !uniqueUserId,
        true
      );

      try {
        const userAuth = await dailyStreakApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId,
          token,
          entityAuthenticationToken,
        });

        if (!userAuth) {
          setIsLoading(false);
          return;
        }

        const headers = {
          apiKey,
          userId: userAuth.userId,
          token: userAuth.token,
        };

        if (questId) {
          const campaignData = await dailyStreakApi.fetchCampaignData(
            entityId,
            questId,
            headers,
            variation
          );

          if (campaignData.sdkConfig?.uiProps?.questThemeData && !initialQuestThemeData) {
            setQuestThemeData(campaignData.sdkConfig.uiProps.questThemeData);
          }

          if (campaignData.sdkConfig?.uiProps?.BrandTheme && !initialBrandTheme) {
            setBrandTheme(campaignData.sdkConfig.uiProps.BrandTheme.properties);
          }

          if (campaignData?.sdkConfig?.uiProps?.stepDetails) {
            setSteps(campaignData.sdkConfig.uiProps.stepDetails.sort((a: any, b: any) => a.range - b.range));
          }

          if (campaignData?.sdkConfig?.uiProps?.showFooter !== undefined) {
            setShowFooter(campaignData.sdkConfig.uiProps.showFooter);
          }
        }

        const count = await dailyStreakApi.getDailyStreakCount(
          entityId,
          userAuth.userId,
          counterId,
          headers
        );

        setDays(count);
        if (count >= steps[steps.length - 1].range) {
          confettiOnCompletion && confetti(confettiDuration || 500);
        }
      } catch (error) {
        console.error("Failed to initialize streak:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeStreak();
  }, [
    apiKey,
    entityId,
    uniqueUserId,
    uniqueEmailId,
    userId,
    token,
    questId,
    counterId,
    variation,
  ]);

  const getCurrentStep = (days: number): number => {
    for (let i = 0; i < steps.length; i++) {
      if (days < steps[i].range) {
        return Math.max(0, i - 1);
      }
    }
    return steps.length - 1;
  };

  const isStepActive = (index: number): boolean => {
    return days >= steps[index].range;
  };

  if (isLoading) return null;

  const currentStep = getCurrentStep(days);

  const contextValue = {
    days,
    currentStep,
    themeManager,
    stepDetails: steps,
    isStepActive,
    pendingStreakImg,
    filledStreakImg,
    footerText,
    footerLink,
    checkedUponCompletion,
    showFooter
  };

  return (
    <StreakContext.Provider value={contextValue}>
      <div
        className="q_daily_streak_cont"
        style={themeManager.getContainerStyles()}
        {...props}
      >
        {children}
      </div>
    </StreakContext.Provider>
  );
};

Streak.Counter = StreakCounter;
Streak.Content = StreakContent;
Streak.Timer = StreakTimer;
Streak.Footer = StreakFooter;

export default Streak;
